/**
 * Locale-aware formatters
 * ============================================================================
 * Wraps Intl.NumberFormat / Intl.DateTimeFormat with helpers that read the
 * current i18n language. Use these everywhere instead of toLocaleString
 * with hardcoded locale strings.
 *
 * Example:
 *   import { useFormatters } from '../utils/format';
 *   const { num, pct, eur, date } = useFormatters();
 *   num(12480)      → "12.480"      (de) | "12,480" (en)
 *   eur(2100000)    → "2.100.000 €" (de) | "€2,100,000" (en)
 *   pct(7.1)        → "7,1 %"       (de) | "7.1%"  (en)
 *   date('2025-12-31') → "31. Dez. 2025" (de) | "31 Dec 2025" (en)
 */

import { useTranslation } from 'react-i18next';

const localeFor = (lang) => (lang === 'de' ? 'de-DE' : 'en-GB');

/** Hook variant, pulls the current i18n language automatically */
export const useFormatters = () => {
  const { i18n } = useTranslation();
  return makeFormatters(i18n.language);
};

/** Standalone variant, pass language explicitly (used by PDF generator) */
export const makeFormatters = (lang) => {
  const locale = localeFor(lang);

  const num = (value, opts = {}) => {
    if (value == null || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat(locale, opts).format(value);
  };

  const pct = (value, decimals = 1) => {
    if (value == null || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value) + (lang === 'de' ? ' %' : '%');
  };

  const eur = (value) => {
    if (value == null || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const currency = (value, code = 'EUR') => {
    if (value == null || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const date = (input, opts = { day: '2-digit', month: 'short', year: 'numeric' }) => {
    if (!input) return 'N/A';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return 'N/A';
    return new Intl.DateTimeFormat(locale, opts).format(d);
  };

  const dateTime = (input) => date(input, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const delta = (value) => {
    if (value == null || isNaN(value)) return 'N/A';
    const sign = value > 0 ? '+' : '';
    return sign + new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  return { num, pct, eur, currency, date, dateTime, delta, locale, lang };
};
